export type PopupButtonActionType = 'sign-out' | 'redirect-to-page' | 'account-suspension' | 'none';

export type PopupButtonActionFormValueType = PopupButtonActionType | '';

export type PopupTriggerTemplateIdFormValueType = string | '';

export type PopupRollingPeriodDaysIsoType = 'P1D' | 'P1W' | 'P1M';

export const POPUP_ROLLING_PERIOD_HOURS_VALUES = [
    'PT1H',
    'PT2H',
    'PT3H',
    'PT4H',
    'PT5H',
    'PT6H',
    'PT7H',
    'PT8H',
    'PT9H',
    'PT10H',
    'PT11H',
    'PT12H',
] as const;

export type PopupRollingPeriodHoursIsoType = (typeof POPUP_ROLLING_PERIOD_HOURS_VALUES)[number];

export type PopupRollingPeriodIsoType = PopupRollingPeriodDaysIsoType | PopupRollingPeriodHoursIsoType;
export type PopupRollingPeriodIsoFormValueType = PopupRollingPeriodIsoType | '';

export type PopupAppliedActionType = 'none' | 'compliance-restriction' | 'deposit-restriction';

export type PopupAppliedActionFormValueType = PopupAppliedActionType | '';

export type PopupComplianceRestrictionPeriodType = '7-days' | '14-days' | '30-days';

export type PopupDepositRestrictionPeriodType =
    | '1-hour'
    | '24-hours'
    | '48-hours'
    | '72-hours'
    | '1-week'
    | 'permanent';

export type PopupAppliedActionRestrictionPeriodFormValueType =
    | PopupComplianceRestrictionPeriodType
    | PopupDepositRestrictionPeriodType
    | '';

export type PopupRiskLevelType = 'low' | 'medium' | 'high';

export type PopupTriggerParamType = {
    name: string;
    displayName: string;
    type: string;
    allowedKinds?: string[];
};

export type PopupTriggerTemplateType = {
    id: string;
    displayName: string;
    description?: string;
    params: PopupTriggerParamType[];
};

export type PopupTriggerParamValueType =
    | number
    | string
    | boolean
    | {
          type: 'rolling';
          value: string;
      }
    | {
          type: 'time-range';
          start: string;
          end: string;
      };

export type PopupTriggerSettingsFormType = {
    templateId: string;
    params: Record<string, PopupTriggerParamValueType>;
};

export type PopupTriggerPeriodType =
    | {
          type: 'rolling';
          value: string;
      }
    | {
          type: 'time-range';
          start: string;
          end: string;
      };

export type PopupTriggerActionType = {
    type: 'automation_action';
    name: string;
};

export type MarkersOfHarmTriggerType = {
    id: number;
    templateId: string;
    name: string;
    params: Record<string, unknown>;
    actions: PopupTriggerActionType[];
    riskLevel: PopupRiskLevelType;
    enabled: boolean;
};

export type PopupButtonFormType = {
    text: string;
    action: PopupButtonActionType;
    redirectUrl?: string;
};

export type MarkersOfHarmPopupPreviewContentType = {
    title?: string;
    bodyText?: string;
    primaryButton?: {
        text: string;
    };
    secondaryButton?: {
        text: string;
    };
};

export type MarkersOfHarmPopupFilterType = 'show-all' | 'active' | 'inactive';

export type MarkersOfHarmPopupSortType = 'title-asc' | 'title-desc' | 'creation-date' | 'last-active-date';

export const MARKERS_OF_HARM_POPUP_FILTER_OPTIONS: Array<{
    id: MarkersOfHarmPopupFilterType;
    label: string;
}> = [
    { id: 'show-all', label: 'Show all' },
    { id: 'active', label: 'Active' },
    { id: 'inactive', label: 'Inactive' },
];

export const MARKERS_OF_HARM_POPUP_SORT_OPTIONS: Array<{
    id: MarkersOfHarmPopupSortType;
    label: string;
}> = [
    { id: 'title-asc', label: 'Title: A to Z' },
    { id: 'title-desc', label: 'Title: Z to A' },
    { id: 'creation-date', label: 'Creation date (Most recent)' },
    { id: 'last-active-date', label: 'Last active date (Most recent)' },
];

/** Matches ~5 lines of title on mobile preview / website (line-clamp: 5). */
export const POPUP_TITLE_MAX_LENGTH = 150;

/** Full-width button labels truncate with ellipsis on website. */
export const POPUP_BUTTON_TEXT_MAX_LENGTH = 45;

export type MarkersOfHarmPopupType = {
    id: string;
    title: string;
    bodyText: string;
    primaryButton: PopupButtonFormType;
    secondaryButton?: PopupButtonFormType;
    triggerId: number;
    active: boolean;
    activationDate?: string;
    deactivationDate?: string;
    createdAt: string;
    trigger?: MarkersOfHarmTriggerType;
};

export const POPUP_PRIMARY_BUTTON_ACTION_OPTIONS: Array<{ label: string; value: PopupButtonActionType }> = [
    { label: 'User Sign out', value: 'sign-out' },
    { label: 'Redirect to page', value: 'redirect-to-page' },
    { label: 'Account suspension', value: 'account-suspension' },
    { label: 'Close only', value: 'none' },
];

export const POPUP_SECONDARY_BUTTON_ACTION_OPTIONS: Array<{ label: string; value: PopupButtonActionType }> = [
    ...POPUP_PRIMARY_BUTTON_ACTION_OPTIONS,
];

export const POPUP_ROLLING_PERIOD_OPTIONS: Array<{ label: string; value: PopupRollingPeriodDaysIsoType }> = [
    { label: '24h rolling', value: 'P1D' },
    { label: '7 days rolling', value: 'P1W' },
    { label: '30 days rolling', value: 'P1M' },
];

export const POPUP_ROLLING_PERIOD_HOURS_OPTIONS: Array<{ label: string; value: PopupRollingPeriodHoursIsoType }> =
    POPUP_ROLLING_PERIOD_HOURS_VALUES.map((value, index) => {
        const hours = index + 1;
        return {
            label: `${hours} ${hours === 1 ? 'hour' : 'hours'} rolling`,
            value,
        };
    });

export const ONLINE_DURATION_TRIGGER_TEMPLATE_ID = 'online-duration';

export const isOnlineDurationTriggerTemplate = (
    template: Pick<PopupTriggerTemplateType, 'id'> | null | undefined
): boolean => template?.id === ONLINE_DURATION_TRIGGER_TEMPLATE_ID;

export const getPopupRollingPeriodOptions = (
    template: Pick<PopupTriggerTemplateType, 'id'> | null | undefined
): Array<{ label: string; value: PopupRollingPeriodIsoType }> => {
    if (isOnlineDurationTriggerTemplate(template)) {
        return POPUP_ROLLING_PERIOD_HOURS_OPTIONS;
    }

    return POPUP_ROLLING_PERIOD_OPTIONS;
};

export const isPopupRollingPeriodValue = (
    value: string,
    template: Pick<PopupTriggerTemplateType, 'id'> | null | undefined
): value is PopupRollingPeriodIsoType => {
    if (isOnlineDurationTriggerTemplate(template)) {
        return POPUP_ROLLING_PERIOD_HOURS_VALUES.some((allowed) => allowed === value);
    }

    return value === 'P1D' || value === 'P1W' || value === 'P1M';
};

export const POPUP_APPLIED_ACTION_OPTIONS: Array<{
    label: string;
    value: PopupAppliedActionType;
    description: string;
}> = [
    {
        label: 'None',
        value: 'none',
        description: 'No consequences are applied to user account.',
    },
    {
        label: 'Compliance restriction',
        value: 'compliance-restriction',
        description: "Apply a compliance restriction to the user's account.",
    },
    {
        label: 'Deposit restriction',
        value: 'deposit-restriction',
        description: 'Prevent the user from making further deposits.',
    },
];

export const POPUP_COMPLIANCE_RESTRICTION_PERIOD_OPTIONS: Array<{
    label: string;
    value: PopupComplianceRestrictionPeriodType;
}> = [
    { label: '7 days', value: '7-days' },
    { label: '14 days', value: '14-days' },
    { label: '30 days', value: '30-days' },
];

export const POPUP_DEPOSIT_RESTRICTION_PERIOD_OPTIONS: Array<{
    label: string;
    value: PopupDepositRestrictionPeriodType;
}> = [
    { label: '1 hour', value: '1-hour' },
    { label: '24 hours', value: '24-hours' },
    { label: '48 hours', value: '48-hours' },
    { label: '72 hours', value: '72-hours' },
    { label: '1 week', value: '1-week' },
    { label: 'Permanent', value: 'permanent' },
];

export const showsAppliedActionRestrictionPeriod = (action: PopupAppliedActionFormValueType): boolean =>
    action === 'compliance-restriction' || action === 'deposit-restriction';

export const getAppliedActionRestrictionPeriodOptions = (
    action: PopupAppliedActionFormValueType
): Array<{ label: string; value: PopupAppliedActionRestrictionPeriodFormValueType }> => {
    if (action === 'compliance-restriction') {
        return POPUP_COMPLIANCE_RESTRICTION_PERIOD_OPTIONS;
    }

    if (action === 'deposit-restriction') {
        return POPUP_DEPOSIT_RESTRICTION_PERIOD_OPTIONS;
    }

    return [];
};

export const POPUP_RISK_LEVEL_OPTIONS: Array<{ label: string; value: PopupRiskLevelType }> = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
];
